// Оплата из кабинета — у гостя через аккаунт по почте, и сразу к оплате. SMY-341
//
// Решение SMY-159: гость не платит — чек по 422-ФЗ уходит на почту или номер, а
// у гостя нет ни того, ни другого, и оплаченный тариф держался бы на одном
// токене вкладки. Решение владельца 21.09.2026 — путь встроен в саму оплату:
// гость, нажавший «Оплатить», видит, что нужен аккаунт, заводит его по почте, а
// после кода сразу попадает на страницу оплаты. Уводить его на экран входа
// нельзя: оттуда кабинет открывается с начала, и выбранный тариф теряется.
//
// Сервер гостю отказывает тоже (`routes/payments.ts`, 402 `signup_required`),
// и окно открывается и на этот отказ: карточка человека могла не загрузиться, и
// экран считал его не гостем.
//
// Экранов с оплатой два — тарифы и лимиты, — и путь у них один: этот хук.
// Правила шага почты и кода общие с экраном входа (`email-code.ts`), токен
// гостя, ставшего аккаунтом, перекладывает сессия (`settle` в session.tsx), уход
// на оплату — `openPayment` (payment.ts).

import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react'
import { CONSENT_NOTICE, countdownLabel, deliveryLabel, isValidEmail, TARIFFS, UNIT_LABELS, type TariffId, type UnitId } from '@smyslika/core'
import { ApiError } from '@smyslika/api-client'

import { emailCodePolicy, failText, requestCode, useCountdown, verifyCode } from './email-code.ts'
import { openPayment } from './payment.ts'
import { useSession } from './session.tsx'
import { Button, Field, Sheet } from './ui/index.ts'

/** Что покупают: тариф или пакет. */
export type Purchase = { kind: 'subscription'; tariff: TariffId } | { kind: 'pack'; unit: UnitId }

/** Текст сервера — законченным предложением: записки API пишутся без точки. */
const sentence = (text: string): string => (/[.!?…]$/.test(text) ? text : `${text}.`)

/** «тарифа «Плюс»» или «пакета вопросов» — для строки «перейдём к оплате …». */
const whatOf = (p: Purchase): string =>
  p.kind === 'subscription' ? `тарифа «${TARIFFS[p.tariff].label}»` : `пакета ${UNIT_LABELS[p.unit].many}`

/**
 * Оплата с экрана.
 *
 * `pay` — нажатие кнопки, `sheet` — окно регистрации, которое экран кладёт в
 * свою разметку, `note` — отказ сервера словами, `onSettled` — после каждой
 * попытки, чтобы экран перечитал остатки.
 */
export function usePurchase(onSettled?: () => void): {
  pay: (p: Purchase) => Promise<void>
  busy: boolean
  note: string | null
  sheet: ReactNode
} {
  const { billing, guest } = useSession()
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState<string | null>(null)
  /** Гость нажал «Оплатить»: что он выбрал, пока заводит аккаунт. */
  const [pending, setPending] = useState<Purchase | null>(null)

  /** Счёт и уход на оплату. Отказ — словами в `note`, отказ гостю — окно. */
  async function checkout(p: Purchase): Promise<void> {
    try {
      const { url } = p.kind === 'subscription' ? await billing.buyTariff(p.tariff) : await billing.buyPack(p.unit)
      openPayment(url)
    } catch (e) {
      if (e instanceof ApiError && e.reason === 'signup_required') {
        setPending(p)
        return
      }
      setNote(failText(e))
    }
  }

  async function pay(p: Purchase): Promise<void> {
    setNote(null)
    if (guest) {
      setPending(p)
      return
    }
    setBusy(true)
    try {
      await checkout(p)
    } finally {
      setBusy(false)
      onSettled?.()
    }
  }

  const sheet =
    pending == null ? null : (
      <SignupToPay
        purchase={pending}
        onClose={() => setPending(null)}
        onSignedUp={async () => {
          setPending(null)
          await checkout(pending)
          onSettled?.()
        }}
      />
    )

  return { pay, busy, note, sheet }
}

/**
 * Окно «нужен аккаунт»: почта → код → оплата.
 *
 * Свободная почта ложится в тот же аккаунт гостя: его работа на месте, и после
 * кода окно сразу уводит на оплату. Почта, на которую уже есть аккаунт, — вход в
 * него: оплата пойдёт от него, а задачи гостя туда не переехали. Это сказать ДО
 * оплаты (записка сервера `note` у исхода `signed_in`), поэтому здесь окно
 * останавливается на третьем шаге и ждёт кнопки — к тому же тарифы у того
 * аккаунта свои, и сервер может отказать «уже подключён».
 */
function SignupToPay({
  purchase,
  onClose,
  onSignedUp,
}: {
  purchase: Purchase
  onClose: () => void
  onSignedUp: () => Promise<void>
}) {
  const { auth, reference, signedInNow } = useSession()
  const { codeLength, cooldown, delivery } = emailCodePolicy(reference)
  const [step, setStep] = useState<'email' | 'code' | 'account'>('email')
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  /** Записка сервера о входе в существующий аккаунт. */
  const [accountNote, setAccountNote] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [waitUntil, setWaitUntil] = useState<number | null>(null)
  const wait = useCountdown(waitUntil)
  const codeField = useRef<HTMLInputElement>(null)

  // Фокус в поле кода при смене шага, как на экране входа: `autoFocus` окна
  // срабатывает только при открытии, и после «Получить код» фокус оставался
  // на странице (живая проверка 21.09.2026).
  useEffect(() => {
    if (step === 'code') codeField.current?.focus()
  }, [step])

  async function ask(e?: FormEvent): Promise<void> {
    e?.preventDefault()
    setError(null)
    setBusy(true)
    const res = await requestCode(auth, email, cooldown)
    if (res.ok || res.waitUntil != null) {
      setStep('code')
      setWaitUntil(res.waitUntil ?? null)
    }
    if (!res.ok) setError(res.error)
    setBusy(false)
  }

  async function confirm(e: FormEvent): Promise<void> {
    e.preventDefault()
    setError(null)
    setBusy(true)
    const res = await verifyCode(auth, email, code)
    if (!res.ok) {
      if (res.clearCode) {
        setCode('')
        codeField.current?.focus()
      }
      setError(res.error)
      setBusy(false)
      return
    }
    // Сессия перечитывает человека и перекладывает токен гостя, ставшего
    // аккаунтом, в постоянное хранилище: страница оплаты откроется новой
    // вкладкой, и без этого возврат с неё завёл бы нового гостя.
    await signedInNow()
    if (res.verified.outcome === 'signed_in') {
      setAccountNote(res.verified.note ?? null)
      setStep('account')
      setBusy(false)
      return
    }
    await onSignedUp()
  }

  return (
    <Sheet open onClose={onClose} closable label="Нужен аккаунт">
      {step === 'email' ? (
        <form onSubmit={(e) => void ask(e)}>
          <div className="onboard__head">
            <h2 className="onboard__title">Нужен аккаунт</h2>
            <p className="onboard__explain">
              Оплаченное привязывается к аккаунту, а чек приходит на почту. Подтверди почту — и сразу
              перейдём к оплате {whatOf(purchase)}. С новой почтой всё сделанное останется в аккаунте.
            </p>
          </div>

          <label className="onboard__label" htmlFor="pay-email">
            Почта
          </label>
          <Field
            id="pay-email"
            boxy
            autoFocus
            type="email"
            inputMode="email"
            value={email}
            autoComplete="email"
            placeholder="имя@почта.ру"
            onChange={(e) => setEmail(e.target.value)}
          />

          {error != null ? (
            <p className="onboard__error" role="alert">
              {error}
            </p>
          ) : null}

          <div className="onboard__actions">
            <Button kind="ghost" onClick={onClose}>
              Отмена
            </Button>
            <Button type="submit" disabled={busy || !isValidEmail(email)}>
              Получить код
            </Button>
          </div>

          {/* Согласие — то же, что под формой входа: аккаунт заводится здесь, и
              без этой строки человек заводил бы его, не увидев оферты. */}
          <p className="onboard__foot">{CONSENT_NOTICE}</p>
        </form>
      ) : step === 'code' ? (
        <form onSubmit={(e) => void confirm(e)}>
          <div className="onboard__head">
            <h2 className="onboard__title">Код из письма</h2>
            <p className="onboard__explain">Отправили на {email}.</p>
          </div>

          <label className="onboard__label" htmlFor="pay-code">
            Код
          </label>
          <Field
            ref={codeField}
            id="pay-code"
            boxy
            autoFocus
            inputMode="numeric"
            value={code}
            autoComplete="one-time-code"
            placeholder={'—'.repeat(codeLength)}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, codeLength))}
          />

          {error != null ? (
            <p className="onboard__error" role="alert">
              {error}
            </p>
          ) : null}

          <div className="onboard__actions">
            {/* Назад, а не «Отмена»: письмо уже ушло, и вернуться человек хочет
                к полю адреса — исправить опечатку, а не выйти. */}
            <Button
              kind="ghost"
              onClick={() => {
                setStep('email')
                setCode('')
                setError(null)
              }}
            >
              Назад
            </Button>
            <Button type="submit" disabled={busy || code.length !== codeLength}>
              Подтвердить и оплатить
            </Button>
          </div>

          <p className="onboard__foot">
            Письмо идёт до {deliveryLabel(delivery)} — загляни и в папку «Спам».{' '}
            {wait > 0 ? (
              `Запросить новый код можно через ${countdownLabel(wait)}.`
            ) : (
              <button type="button" className="signin__link" onClick={() => void ask()}>
                Отправить код заново
              </button>
            )}
          </p>
        </form>
      ) : (
        <div>
          <div className="onboard__head">
            <h2 className="onboard__title">Аккаунт уже есть</h2>
            <p className="onboard__explain">
              {sentence(accountNote ?? 'На этот адрес уже есть аккаунт — вошли в него')} Оплата {whatOf(purchase)}{' '}
              пойдёт от этого аккаунта.
            </p>
          </div>

          {error != null ? (
            <p className="onboard__error" role="alert">
              {error}
            </p>
          ) : null}

          <div className="onboard__actions">
            <Button kind="ghost" onClick={onClose}>
              Не сейчас
            </Button>
            <Button
              disabled={busy}
              onClick={() => {
                setBusy(true)
                void onSignedUp()
              }}
            >
              Перейти к оплате
            </Button>
          </div>
        </div>
      )}
    </Sheet>
  )
}
